import { ChangeDetectionStrategy, Component, inject, input, OnDestroy } from '@angular/core';
import { ItemManagementService } from '@app/cem/project/services/item-management.service';
import { kendoIcons } from '@app/shared/library/kendo-icons';
import { ButtonDirective } from '@progress/kendo-angular-buttons';
import { Subscription } from 'rxjs';
import { DragDropItemBaseComponent } from '../../drag-drop/drag-drop-item-base/drag-drop-item-base.component';
import { DragDropScopingItemComponent } from '../../drag-drop/drag-drop-scoping-item/drag-drop-scoping-item.component';
import { IDependentItem } from '../../models/idependent-item';
import { IItemScratchPadChangeEventType } from '../../models/iitem-scratch-pad-change-event-type';
import { IScopingItemGroupMapping } from '../../models/iscoping-item-group-mapping';

@Component({
  selector: 'app-scoping-items-pad',
  templateUrl: './scoping-items-pad.component.html',
  styleUrls: ['./scoping-items-pad.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonDirective,
    DragDropScopingItemComponent,
  ],
})
export class ScopingItemsPadComponent extends DragDropItemBaseComponent implements OnDestroy {
  private itemManagementService = inject(ItemManagementService);

  public icons = kendoIcons;
  public groups: IScopingItemGroupMapping[] = [];
  public saving: boolean = false;

  private scratchPadChangeEventSub: Subscription;
  private addDependentItemEventSub: Subscription;
  private updateDependentItemEventSub: Subscription;
  private deleteDependentItemEventSub: Subscription;
  private recordChangedEventSub: Subscription;

  private group: IScopingItemGroupMapping;

  public layout: string = 'grid';
  public columnCount: number = 3;

  constructor() {
    super();

    this.scratchPadChangeEventSub = this.brokerService.ScratchPadChangeEvent.subscribe(s => {
      if (!s) return;

      switch (s.type) {
        case IItemScratchPadChangeEventType.Created:
          this.addGroup(s.data);
          break;
        case IItemScratchPadChangeEventType.Deleted:
          this.removeGroup(s.data);
          break;
        default:
          break;
      }
    });

    this.addDependentItemEventSub = this.brokerService.AddDependentItemEvent.subscribe(s => {
      if (!s) return;
      this.addDependentItem(s.dependentItem, s.destScopingItemId);
    });

    this.updateDependentItemEventSub = this.brokerService.UpdateDependentItemEvent.subscribe(s => {
      if (!s) return;
      this.updateDependentItem(s.dependentItem, s.destScopingItemId);
    });

    this.deleteDependentItemEventSub = this.brokerService.DeleteDependentItemEvent.subscribe(s => {
      if (!s) return;
      this.DeleteDependentItem(s.dependentItem, s.destScopingItemId);
    });

    this.recordChangedEventSub = this.brokerService.RecordChangedEvent.subscribe(s => {
      if (s == null) return;
      this.changesMade = this.brokerService.anyChanges;
    });
  }

  ngOnDestroy() {
    if (this.scratchPadChangeEventSub) {
      this.scratchPadChangeEventSub.unsubscribe();
    }

    if (this.addDependentItemEventSub) {
      this.addDependentItemEventSub.unsubscribe();
    }

    if (this.updateDependentItemEventSub) {
      this.updateDependentItemEventSub.unsubscribe();
    }

    if (this.deleteDependentItemEventSub) {
      this.deleteDependentItemEventSub.unsubscribe();
    }

    if (this.recordChangedEventSub) {
      this.recordChangedEventSub.unsubscribe();
    }
  }

  public saveChanges() {
    let changes = this.brokerService.RecordChangedEvent.value;
    if (changes) {
      this.saving = true;
      this.itemManagementService.saveItemGroupMappings(changes.ItemGroupMappings).subscribe(() => {
        this.saving = false;
        this.clear(true);
      }, (e) => {
        this.saving = false;
        return;
      });
    }
  }

  public clear(forceReload: boolean) {
    this.groups.length = 0;
    this.brokerService.clearChanges();
    this.brokerService.ResetEvent.next({ forceReload: forceReload });
  }

  public removeGroup(group: IScopingItemGroupMapping) {
    this.groups = this.groups.filter(f => f.ScopingItemId !== group.ScopingItemId);
  }

  private addGroup(newGroup: IScopingItemGroupMapping) {
    let duplicateGroup: boolean = false;

    this.groups.forEach((existingGroup) => {
      if (existingGroup.ScopingItemId == newGroup.ScopingItemId)
        duplicateGroup = true;
    });

    if (!duplicateGroup) {
      this.groups.push(newGroup);
    }
  }

  private addDependentItem(dependentItem: IDependentItem, destScopingItemId: number) {

    this.group = this.groups.find(f => f.ScopingItemId === destScopingItemId);

    // If the dependent item already exists under a scoping item, do not update the group.
    if (this.group.MappedDependentItems.find(f => f.DependentItemId === dependentItem.DependentItemId) != null) {
      return;
    }

    this.group.AddDependentItem(dependentItem);
    this.brokerService.updateGroup(this.group);
  }

  private updateDependentItem(dependentItem: IDependentItem, destScopingItemId: number) {
    this.group = this.groups.find(f => f.ScopingItemId === destScopingItemId);
    this.group.UpdateDependentItem(dependentItem);
    this.brokerService.updateGroup(this.group);
  }

  private DeleteDependentItem(dependentItem: IDependentItem, destScopingItemId: number) {
    this.group = this.groups.find(f => f.ScopingItemId === destScopingItemId);
    this.group.RemoveDependentItem(dependentItem);
    this.brokerService.updateGroup(this.group);
  }
}